'use client';

import type { AbstractIntlMessages } from 'next-intl';
import { NextIntlClientProvider } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import type { Locale } from '@/i18n/config';
import { defaultTimeZone } from '@/i18n/locales';
import { logger } from '@/lib/telemetry/logger';
import { useLocaleStore } from '@/stores/locale';

const LOCALE_LOADERS: Record<Locale, () => Promise<AbstractIntlMessages>> = {
  en: () => import('@/i18n/locales/en.json').then((m) => m.default as AbstractIntlMessages),
  es: () => import('@/i18n/locales/es.json').then((m) => m.default as AbstractIntlMessages),
  fr: () => import('@/i18n/locales/fr.json').then((m) => m.default as AbstractIntlMessages),
  zh: () => import('@/i18n/locales/zh.json').then((m) => m.default as AbstractIntlMessages),
  ja: () => import('@/i18n/locales/ja.json').then((m) => m.default as AbstractIntlMessages),
  ko: () => import('@/i18n/locales/ko.json').then((m) => m.default as AbstractIntlMessages),
  pt: () => import('@/i18n/locales/pt.json').then((m) => m.default as AbstractIntlMessages),
  de: () => import('@/i18n/locales/de.json').then((m) => m.default as AbstractIntlMessages),
  hi: () => import('@/i18n/locales/hi.json').then((m) => m.default as AbstractIntlMessages),
  ru: () => import('@/i18n/locales/ru.json').then((m) => m.default as AbstractIntlMessages),
  tr: () => import('@/i18n/locales/tr.json').then((m) => m.default as AbstractIntlMessages),
  it: () => import('@/i18n/locales/it.json').then((m) => m.default as AbstractIntlMessages),
  id: () => import('@/i18n/locales/id.json').then((m) => m.default as AbstractIntlMessages),
  vi: () => import('@/i18n/locales/vi.json').then((m) => m.default as AbstractIntlMessages),
  nl: () => import('@/i18n/locales/nl.json').then((m) => m.default as AbstractIntlMessages),
  pl: () => import('@/i18n/locales/pl.json').then((m) => m.default as AbstractIntlMessages),
  th: () => import('@/i18n/locales/th.json').then((m) => m.default as AbstractIntlMessages),
  uk: () => import('@/i18n/locales/uk.json').then((m) => m.default as AbstractIntlMessages),
  ro: () => import('@/i18n/locales/ro.json').then((m) => m.default as AbstractIntlMessages),
  tl: () => import('@/i18n/locales/tl.json').then((m) => m.default as AbstractIntlMessages),
  sv: () => import('@/i18n/locales/sv.json').then((m) => m.default as AbstractIntlMessages),
};

async function loadMessages(locale: Locale): Promise<AbstractIntlMessages> {
  return LOCALE_LOADERS[locale]();
}

interface LocaleProviderProps {
  initialLocale: Locale;
  initialMessages: AbstractIntlMessages;
  children: React.ReactNode;
}

export function LocaleProvider({
  initialLocale,
  initialMessages,
  children,
}: LocaleProviderProps): React.ReactNode {
  const storeLocale = useLocaleStore((s) => s.locale);
  const [messages, setMessages] = useState<AbstractIntlMessages>(initialMessages);
  const [activeLocale, setActiveLocale] = useState<Locale>(initialLocale);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;

      // On mount, if the store locale (read from localStorage) differs from
      // what the server rendered, load the correct messages immediately.
      // This handles back-navigation where Next.js serves a stale RSC payload.
      if (storeLocale !== initialLocale) {
        let cancelled = false;

        async function syncOnMount(): Promise<void> {
          try {
            const newMessages = await loadMessages(storeLocale);
            if (!cancelled) {
              setMessages(newMessages);
              setActiveLocale(storeLocale);
            }
          } catch (err: unknown) {
            logger.warn('Failed to load locale messages on mount sync', {
              locale: storeLocale,
              error: err instanceof Error ? err.message.slice(0, 200) : String(err),
            });
          }
        }

        void syncOnMount();

        return (): void => {
          cancelled = true;
        };
      }
      return;
    }

    // After mount: react to store locale changes from LanguageSwitcher
    if (storeLocale === activeLocale) return;

    let cancelled = false;

    async function loadAndSet(): Promise<void> {
      try {
        const newMessages = await loadMessages(storeLocale);
        if (!cancelled) {
          setMessages(newMessages);
          setActiveLocale(storeLocale);
        }
      } catch (err: unknown) {
        logger.warn('Failed to load locale messages', {
          locale: storeLocale,
          error: err instanceof Error ? err.message.slice(0, 200) : String(err),
        });
      }
    }

    void loadAndSet();

    return (): void => {
      cancelled = true;
    };
  }, [storeLocale, activeLocale, initialLocale]);

  return (
    <NextIntlClientProvider locale={activeLocale} messages={messages} timeZone={defaultTimeZone}>
      {children}
    </NextIntlClientProvider>
  );
}
